import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../lib/axios';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * Datos de empresa que salen impresos en el encabezado y el pie de cada
 * cotización. Sólo admin/manager pueden editarlos: acá viven los datos
 * bancarios a los que el cliente transfiere.
 */
export default function QuoteProfileForm() {
    const qc = useQueryClient();
    const role = useAuthStore((s) => s.user?.role);
    const canEdit = ['admin', 'manager'].includes(role);

    const [form, setForm] = useState(null);
    const [saved, setSaved] = useState(false);
    const logoInput = useRef(null);

    const { data: resp, isLoading } = useQuery({
        queryKey: ['quote-profile'],
        queryFn: () => axios.get('/api/quote-profile'),
    });

    useEffect(() => {
        if (resp?.data) setForm(resp.data);
    }, [resp]);

    const saveMutation = useMutation({
        mutationFn: (payload) => axios.put('/api/quote-profile', payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['quote-profile'] });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        },
        onError: (e) => alert(e.response?.data?.message || 'No se pudo guardar el perfil.'),
    });

    const logoMutation = useMutation({
        mutationFn: (file) => {
            const fd = new FormData();
            fd.append('logo', file);
            return axios.post('/api/quote-profile/logo', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['quote-profile'] }),
        onError: (e) => alert(e.response?.data?.message || 'No se pudo subir el logo.'),
    });

    const removeLogoMutation = useMutation({
        mutationFn: () => axios.delete('/api/quote-profile/logo'),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['quote-profile'] }),
    });

    if (isLoading || !form) {
        return <p className="text-sm text-gray-400">Cargando perfil...</p>;
    }

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = (e) => {
        e.preventDefault();
        const { has_logo, ...payload } = form;
        saveMutation.mutate(payload);
    };

    return (
        <form onSubmit={submit} className="max-w-3xl space-y-6">
            {!canEdit && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <span className="material-symbols-outlined text-amber-600 text-lg">lock</span>
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                        Sólo un administrador puede modificar estos datos, porque incluyen la cuenta
                        bancaria que aparece impresa en cada cotización.
                    </p>
                </div>
            )}

            <Section title="Identidad" subtitle="Aparece en el encabezado del PDF.">
                <Field label="Razón social" value={form.company_name} onChange={set('company_name')} disabled={!canEdit} />
                <Field label="Dirección" value={form.address} onChange={set('address')} disabled={!canEdit} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Número de licencia" value={form.license_number} onChange={set('license_number')} disabled={!canEdit} />
                    <Field label="TRN" value={form.trn} onChange={set('trn')} disabled={!canEdit} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Field label="Sitio web" value={form.website} onChange={set('website')} disabled={!canEdit} />
                    <Field label="Email" value={form.email} onChange={set('email')} disabled={!canEdit} />
                    <Field label="Teléfono" value={form.phone} onChange={set('phone')} disabled={!canEdit} />
                </div>

                <div className="flex items-center gap-3 pt-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Logo</span>
                    {form.has_logo
                        ? <span className="text-xs text-emerald-600 flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">check_circle</span> Cargado
                          </span>
                        : <span className="text-xs text-gray-400">Sin logo</span>}
                    {canEdit && (
                        <>
                            <input ref={logoInput} type="file" accept="image/png,image/jpeg" className="hidden"
                                onChange={(e) => { if (e.target.files?.[0]) logoMutation.mutate(e.target.files[0]); e.target.value = ''; }} />
                            <button type="button" onClick={() => logoInput.current?.click()} disabled={logoMutation.isPending}
                                className="text-xs font-bold text-blue-600 hover:underline disabled:opacity-50">
                                {logoMutation.isPending ? 'Subiendo...' : (form.has_logo ? 'Cambiar' : 'Subir')}
                            </button>
                            {form.has_logo && (
                                <button type="button" onClick={() => removeLogoMutation.mutate()}
                                    className="text-xs font-bold text-gray-400 hover:text-red-500">Quitar</button>
                            )}
                        </>
                    )}
                </div>
            </Section>

            <Section title="Documento" subtitle="Cómo se titula y se numera la cotización.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Título del documento" value={form.document_title} onChange={set('document_title')}
                        disabled={!canEdit} placeholder="PROFORMA INVOICE" />
                    <Field label="Prefijo de numeración" value={form.number_prefix} onChange={set('number_prefix')}
                        disabled={!canEdit} placeholder="FZQ" hint="Sólo afecta a las cotizaciones nuevas." />
                </div>
            </Section>

            <Section title="Bloques del pie" subtitle="Se imprimen al final de cada cotización. Una línea por concepto.">
                <TextArea label="Documentación requerida" value={form.documents_required} onChange={set('documents_required')} disabled={!canEdit} />
                <TextArea label="Plazos estimados" value={form.timelines} onChange={set('timelines')} disabled={!canEdit} />
                <TextArea label="Datos bancarios" value={form.bank_details} onChange={set('bank_details')} disabled={!canEdit} />
                <TextArea label="Términos y condiciones" value={form.terms} onChange={set('terms')} disabled={!canEdit} />
            </Section>

            {canEdit && (
                <div className="flex items-center gap-3">
                    <button type="submit" disabled={saveMutation.isPending}
                        className="py-2.5 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-50">
                        {saveMutation.isPending ? 'Guardando...' : 'Guardar perfil'}
                    </button>
                    {saved && (
                        <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                            <span className="material-symbols-outlined text-base">check_circle</span> Guardado
                        </span>
                    )}
                </div>
            )}
        </form>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl p-5 space-y-3">
            <div>
                <h3 className="text-sm font-black dark:text-white">{title}</h3>
                {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}

function Field({ label, hint, ...props }) {
    return (
        <label className="block">
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">{label}</span>
            <input
                {...props}
                className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white disabled:opacity-60"
            />
            {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
        </label>
    );
}

function TextArea({ label, ...props }) {
    return (
        <label className="block">
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">{label}</span>
            <textarea
                rows={4}
                {...props}
                className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white resize-none disabled:opacity-60"
            />
        </label>
    );
}
